// ============================================================
//  ResolveChatSheet.js — "Resolve this chat" (Website Widget → Chats)
//
//  Optional goodbye message + optional "How did we do?" rating card for the
//  visitor. Choices are remembered on this device. Ctrl/⌘ + Enter resolves.
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PREF_KEY = 'ck_lc_resolve';
const TEMPLATES = [
  'Thanks for chatting with us! Have a lovely day 👋',
  'Glad I could help! Message us here any time.',
  'Your issue is sorted. Thanks for your patience 🙏',
];

function loadPrefs() {
  try {
    const p = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
    return { goodbye: p.goodbye !== false, text: typeof p.text === 'string' && p.text ? p.text : TEMPLATES[0], rating: p.rating !== false };
  } catch { return { goodbye: true, text: TEMPLATES[0], rating: true }; }
}
function savePrefs(p) {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

function Toggle({ on, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className={`lcr-switch ${on ? 'is-on' : ''}`} onClick={() => onChange(!on)}>
      <i />
    </button>
  );
}

export default function ResolveChatSheet({ name, replyAs, msgCount, startedAt, blocked, onCancel, onConfirm }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const textRef = useRef(null);
  const sheetRef = useRef(null);
  const set = (k, v) => setPrefs(p => ({ ...p, [k]: v }));

  const confirmRef = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    const prev = document.activeElement;
    sheetRef.current?.querySelector('.lcr-go')?.focus();
    const key = e => {
      if (e.key === 'Escape') { e.preventDefault(); cancelRef.current(); }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); confirmRef.current(); }
      if (e.key === 'Tab' && sheetRef.current) {             // keep focus inside the sheet
        const f = sheetRef.current.querySelectorAll('button, textarea');
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener('keydown', key);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', key); document.body.style.overflow = overflow; prev?.focus?.(); };
  }, []);

  function confirm() {
    const text = prefs.text.trim();
    savePrefs({ goodbye: prefs.goodbye, text: text || TEMPLATES[0], rating: prefs.rating });
    onConfirm({ message: !blocked && prefs.goodbye ? text : '', rating: !blocked && prefs.rating });
  }
  confirmRef.current = confirm;

  const goodbyeOn = prefs.goodbye && !blocked;
  const ratingOn = prefs.rating && !blocked;
  const started = startedAt ? new Date(String(startedAt).replace(' ', 'T')) : null;

  return createPortal(
    <div className="wwg-vars lcr-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="lcr-sheet" role="dialog" aria-modal="true" aria-labelledby="lcr-title" ref={sheetRef}>
        <span className="lcr-grab" aria-hidden="true" />
        <div className="lcr-hero">
          <span className="lcr-ring" aria-hidden="true"><i className="fa-solid fa-check" /></span>
          <h2 id="lcr-title">Resolve this chat?</h2>
          <p>
            <b>{name}</b>
            {msgCount > 0 && <> · {msgCount} message{msgCount === 1 ? '' : 's'}</>}
            {started && !Number.isNaN(started.getTime()) && <> · started {started.toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</>}
          </p>
        </div>

        {blocked ? (
          <div className="lcr-opt is-muted"><i className="fa-solid fa-ban" /> This visitor is blocked, so nothing is sent to them.</div>
        ) : (
          <>
            <div className={`lcr-opt ${goodbyeOn ? 'is-on' : ''}`}>
              <div className="lcr-opt-row">
                <span className="lcr-opt-ic"><i className="fa-regular fa-hand" /></span>
                <div className="lcr-opt-txt"><b>Send a goodbye message</b><span>Sent as {replyAs ? <b>{replyAs}</b> : 'your team'} before the chat closes</span></div>
                <Toggle on={goodbyeOn} onChange={v => set('goodbye', v)} label="Send a goodbye message" />
              </div>
              {goodbyeOn && (
                <div className="lcr-opt-body">
                  <textarea ref={textRef} rows={2} maxLength={2000} value={prefs.text} onChange={e => set('text', e.target.value)} aria-label="Goodbye message" />
                  <div className="lcr-chips">
                    {TEMPLATES.map(t => (
                      <button key={t} type="button" className={prefs.text === t ? 'is-on' : ''} onClick={() => { set('text', t); textRef.current?.focus(); }}>{t}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={`lcr-opt ${ratingOn ? 'is-on' : ''}`}>
              <div className="lcr-opt-row">
                <span className="lcr-opt-ic is-star"><i className="fa-solid fa-star" /></span>
                <div className="lcr-opt-txt"><b>Ask for a rating</b><span>They see a quick 1–5 star card in the chat</span></div>
                <Toggle on={ratingOn} onChange={v => set('rating', v)} label="Ask for a rating" />
              </div>
              {ratingOn && (
                <div className="lcr-preview" aria-hidden="true">
                  <b>How did we do?</b>
                  <span>{[1, 2, 3, 4, 5].map(n => <i key={n} className="fa-solid fa-star" style={{ animationDelay: `${n * 60}ms` }} />)}</span>
                </div>
              )}
            </div>
          </>
        )}

        <div className="lcr-foot">
          <button type="button" className="wwg-btn wwg-btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="wwg-btn lcr-go" onClick={confirm}>
            <i className="fa-solid fa-check" /> Resolve chat <kbd>Ctrl ↵</kbd>
          </button>
        </div>
        <p className="lcr-note">You’ll have 5 seconds to undo. After that the visitor can only rate the chat — not reply. You can reopen it any time.</p>
      </div>
    </div>,
    document.body
  );
}
